/**
 * Helper functions for formatting weather data for display
 */

/**
 * Rounds a numeric weather value to a whole number for display purposes.
 * Returns undefined if the input is undefined or null.
 *
 * @param value - The numeric value to round
 * @returns The rounded value or undefined
 */
export const roundWeatherValue = (
  value: number | undefined | null
): number | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  return Math.round(value * 10) / 10;
};

/**
 * Formats a temperature value for display with unit.
 *
 * @param temperature - The temperature value
 * @param unit - The temperature unit (e.g., 'F', 'C')
 * @param fallback - The fallback string when value is not available (default: '---')
 * @returns Formatted temperature string (e.g., "72°F") or fallback
 */
export const formatTemperatureDisplay = (
  temperature: number | undefined | null,
  unit?: string,
  fallback: string = '---'
): string => {
  const rounded = roundWeatherValue(temperature);
  if (rounded === undefined) {
    return fallback;
  }
  return `${rounded}°${unit || 'F'}`;
};

/**
 * Formats a humidity value for display with percent sign.
 *
 * @param humidity - The humidity value
 * @param fallback - The fallback string when value is not available (default: '---')
 * @returns Formatted humidity string (e.g., "65%") or fallback
 */
export const formatHumidityDisplay = (
  humidity: number | undefined | null,
  fallback: string = '---'
): string => {
  const rounded = roundWeatherValue(humidity);
  if (rounded === undefined) {
    return fallback;
  }
  return `${rounded}%`;
};

/**
 * Formats a wind speed value for display with unit.
 *
 * @param windSpeed - The wind speed value
 * @param unit - The wind speed unit (e.g., 'mph', 'kph')
 * @returns Formatted wind speed string (e.g., "15 MPH") or undefined if no value
 */
export const formatWindSpeedDisplay = (
  windSpeed: number | undefined | null,
  unit?: string
): string | undefined => {
  const rounded = roundWeatherValue(windSpeed);
  if (rounded === undefined) {
    return undefined;
  }
  const unitDisplay = unit?.toUpperCase() || 'MPH';
  return `${rounded} ${unitDisplay}`;
};
